<script setup lang="ts">
/**
 * @param {string} color - tabBar-未选中[后面改成从pinia中取，此处假定一个主题色仅作效果模拟]
 * @param {string} selectedColor - tabBar-选中 color
 * @param {string} selectedName - 默认tabBar-选中 tagName
 * @param {string} walletIconColor - 钱包主色调
 * @param {event} tabBarEvent - tabBar-自定义事件监听
 */
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { BottomTabBarType, getBottomTabBarList, NavNameEnum } from './footerConfig';
import { storeToRefs, useActivityStore, useAppStore, useTenantStore, useUserStore, useWalletStore } from '@star/stores';
import { emitter, EventEnum, useSessionStorage } from '@star/utils';
import { StorageKeysEnum } from '@star/constants';
import { filter, find, includes, map } from 'lodash-es';
import { useTimeout } from '@vueuse/core';

type TabBarMenus = BottomTabBarType & { ready: boolean };

const { tenantInfo } = storeToRefs(useTenantStore());
const router = useRouter();
const route = useRoute();
const activeMenu = useSessionStorage<string>(StorageKeysEnum.APP_ACTIVE_FOOTER_TAB_MENU, NavNameEnum.Home);
const { template, theme } = storeToRefs(useAppStore());
const activeTabBarRoutes = ref<Record<string, string>>({});
const tabList = ref(getBottomTabBarList(template.value, theme.value));
const userStore = useUserStore();
const walletStore = useWalletStore();
const { isLogin } = storeToRefs(userStore);
const { maxDepositBonusPercentage } = storeToRefs(walletStore);
const { waitReceiveNum } = storeToRefs(useActivityStore());
const { getWaitReceiveNum } = useActivityStore();
const countMap = ref<Record<string, any>>({
  [NavNameEnum.Mine]: 0,
  [NavNameEnum.Deposit]: 0,
});
const isShowBadge = ref<boolean>(false);
let prevMenu = route.name as string;

const { ready: homeReady, start: homeStart } = useTimeout(600, { controls: true });
const { ready: videoReady, start: videoStart } = useTimeout(600, { controls: true });
const { ready: depositReady, start: depositStart } = useTimeout(600, { controls: true });
const { ready: activityReady, start: activityStart } = useTimeout(600, { controls: true });
const { ready: mineReady, start: mineStart } = useTimeout(600, { controls: true });

const readyMaps = computed(() => {
  return {
    [NavNameEnum.Home]: homeReady.value,
    [NavNameEnum.Video]: videoReady.value,
    [NavNameEnum.Deposit]: depositReady.value,
    [NavNameEnum.Activity]: activityReady.value,
    [NavNameEnum.Mine]: mineReady.value,
  };
});

// 过滤掉不显示的菜单项
const tabBarMenus = computed((): TabBarMenus[] => {
  return filter(tabList.value, item => {
    return !(item.name === NavNameEnum.Video && tenantInfo.value.isOpenVideo !== 'true');
  });
});

// 切换 Tab
const onTabChange = async (_name: NavNameEnum) => {
  activeMenu.value = _name;
  setIconAnimation(_name);
  prevMenu = _name;
  const { path, query } = find(tabBarMenus.value, item => item.name === _name) as BottomTabBarType;
  await router.push(activeTabBarRoutes[_name] ? { path: activeTabBarRoutes[_name] } : { path, query });
  if (_name === NavNameEnum.Home) {
    emitter.emit(EventEnum.HOME_PAGE_SCROLL);
  }
};

const setIconAnimation = (_name: NavNameEnum) => {
  console.log(_name, prevMenu);
  switch (_name) {
    case NavNameEnum.Home:
      homeStart();
      break;
    case NavNameEnum.Video:
      videoStart();
      break;
    case NavNameEnum.Deposit:
      depositStart();
      break;
    case NavNameEnum.Activity:
      activityStart();
      break;
    case NavNameEnum.Mine:
      mineStart();
      break;
  }
  switch (prevMenu) {
    case NavNameEnum.Home:
      homeStart();
      break;
    case NavNameEnum.Video:
      videoStart();
      break;
    case NavNameEnum.Deposit:
      depositStart();
      break;
    case NavNameEnum.Activity:
      activityStart();
      break;
    case NavNameEnum.Mine:
      mineStart();
      break;
  }
};

// 切换前检查登录
const beforeChange = (name: string) => {
  const nextRoute = find(router.getRoutes(), item => item.name === name);
  const _name = route.name as string;
  activeTabBarRoutes[_name] = route.fullPath;
  const result = nextRoute?.meta?.login ? isLogin.value : true;
  if (!result) {
    userStore.showLogin({ type: 1, redirect: nextRoute?.path });
  }
  return result;
};

// RECHARGE_NAVIGATION_CORNER_MARK
const getConfigInfo = async () => {
  const { value } = await walletStore.getSysConfig('RECHARGE_NAVIGATION_CORNER_MARK');
  console.log(value, 'RECHARGE_NAVIGATION_CORNER_MARK');
  isShowBadge.value = +value === 1;
};

watch(
  () => route.name,
  (value: string) => {
    activeMenu.value =
      value &&
      includes(
        map(tabBarMenus.value, item => item.name),
        value
      )
        ? value
        : '';
  },
  { immediate: true }
);

watch(
  () => waitReceiveNum.value,
  (value: number) => {
    countMap.value[NavNameEnum.Mine] = value;
  },
  { immediate: true }
);

watch(
  () => [maxDepositBonusPercentage.value, isShowBadge.value],
  (values: number[]) => {
    countMap.value[NavNameEnum.Deposit] = values[0] && values[1] ? `送${values[0]}%` : 0;
  },
  { immediate: true }
);

watch(
  () => isLogin.value,
  async value => {
    if (value) {
      await getConfigInfo();
      await getWaitReceiveNum();
      await walletStore.getPayChannelList();
    } else {
      countMap.value[NavNameEnum.Mine] = 0;
      countMap.value[NavNameEnum.Deposit] = 0;
    }
  },
  { immediate: true }
);
</script>

<template>
  <van-tabbar
    class="tab-menus-wrapper"
    v-model="activeMenu"
    active-color="var(--theme-btn-primary-background)"
    @change="onTabChange"
    safe-area-inset-bottom
    :before-change="beforeChange"
  >
    <van-tabbar-item v-for="item in tabBarMenus" :key="item.title" :name="item.name">
      <template #icon="props">
        <van-badge :class="{ 'not-badge': !countMap[item.name] }" :content="countMap[item.name]">
          <template v-if="theme !== 'dark'">
            <div class="icon-box">
              <img class="tab-icon default" :src="`/images/footer/${item.icon}`" alt="" />
              <img
                class="tab-icon"
                :class="{ show: props.active && !readyMaps[item.name] }"
                :src="`/images/footer/${item.animationInIcon}`"
                alt=""
              />
              <img
                class="tab-icon"
                :class="{ show: !props.active && !readyMaps[item.name] }"
                :src="`/images/footer/${item.animationOutIcon}`"
                alt=""
              />
              <img
                class="tab-icon"
                :class="{ show: props.active && readyMaps[item.name] }"
                :src="`/images/footer/${item.activeIcon}`"
                alt=""
              />
            </div>
          </template>
          <SvgIcon :iconName="props.active ? item.activeIcon : item.icon" :width="55" :height="55" v-else />
        </van-badge>
      </template>
      <span class="title">{{ item.title }}</span>
    </van-tabbar-item>
  </van-tabbar>
</template>

<style scoped lang="less">
.tab-menus-wrapper {
  background: var(--theme-tabbar-background);
  box-shadow: 0 15px 9px var(--theme-backgroud-color);
  z-index: 1000;
  width: 100vw;
  height: var(--footer-height);
  bottom: 3px;

  :deep(.van-tabbar-item) {
    color: var(--theme-icon-color-2);
    padding-top: 24px;

    &__icon {
      margin-bottom: 0;
      margin-top: 15px;
    }

    .title {
      font-size: 40px;
      font-weight: 400;
    }

    .svg-icon {
      color: var(--theme-icon-color-2);
    }

    .icon-box {
      width: 64px;
      height: 64px;
      position: relative;
    }

    .tab-icon {
      position: absolute;
      height: 64px;
      visibility: hidden;
      opacity: 0;

      &.default {
        visibility: visible;
        opacity: 1;
      }

      &.show {
        visibility: visible;
        opacity: 1;
      }
    }

    &.van-tabbar-item {
      &--active {
        .svg-icon {
          color: var(--theme-btn-primary-background);
        }
      }

      &__text {
        height: 90px;
      }
    }

    .van-badge {
      &__wrapper {
        display: block;
      }

      &--top-right {
        transform: translate(50%, -50%) scale(0.8);
        top: -9px;
      }
    }
  }
}
</style>
